import { CSSProperties, ReactNode, Suspense, cloneElement } from 'react'

import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { createRoot } from 'react-dom/client'

type DisplayHint = 'popup' | 'inline'

type EmbeddedRenderOptions = {
  parent: HTMLElement
  shadowHostStyle?: CSSProperties
  scriptElement?: HTMLScriptElement
  /**
   * 埋め込みスタイルの初期ヒント。
   * - 'inline': スクリプトタグが body 内にあり、SLIDER/TILE 表示の可能性が高い
   * - 'popup':  スクリプトタグが head 内にあり、POPUP 型で全画面オーバーレイ
   * `displayType` が scenario から到着する前にレイアウト領域を確保する用途。
   * CLS (Cumulative Layout Shift) を 0 に抑えるために必須。
   */
  displayHint?: DisplayHint
}

const FONT_LINK_ID = 'gai-agents-font'
// font-display=swap: フォント未到着時はフォールバックで即時描画し、到着後に Noto Sans JP へ差し替える。
// モーダル本体は Shadow DOM 内で .pc-modal-backdrop / .sp-modal に Noto Sans JP を明示しており、
// optional だと到着が間に合わなかった場合にホストサイトの sans-serif にフォールバックして
// 見た目が環境依存になる。swap にして moovy ブランドのフォントを確実に適用する。
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap'

export default function embeddedRender(
  node: ReactNode,
  { parent, shadowHostStyle, scriptElement, displayHint = 'popup' }: EmbeddedRenderOptions,
) {
  // ホストWebサイトのCSSの影響を受けないように、Shadow DOM を作成し、その中にレンダリングする
  const shadowHost = parent.appendChild(document.createElement('div'))

  // ロード完了前の FOUC を防止。可視化は tailwind.css load 時。
  shadowHost.style.visibility = 'hidden'

  if (displayHint === 'inline') {
    // nothing
  } else {
    // POPUP 型は全画面オーバーレイなので親ページのレイアウトに影響しない。
    // 初期から fixed + pointer-events: none で配置する。
    // 32bit 整数の最大値で先方サイトの sticky ヘッダー等を確実に上回る。
    shadowHost.style.position = 'fixed'
    shadowHost.style.inset = '0'
    shadowHost.style.pointerEvents = 'none'
    shadowHost.style.zIndex = '2147483647'
  }

  if (shadowHostStyle) {
    Object.assign(shadowHost.style, shadowHostStyle)
  }
  const shadowRoot = shadowHost.attachShadow({ mode: 'open' })
  const container = shadowRoot.appendChild(document.createElement('div'))
  container.id = `gai-agents-${Math.random().toString(36).slice(-8)}`

  if (displayHint === 'inline') {
    container.style.position = 'static'
    container.style.display = 'block'
    container.style.width = '100%'
    container.style.height = '100%'
    container.style.pointerEvents = 'auto'
  } else {
    // POPUP: 子要素の position: fixed が画面基準で効くように container を全画面に広げる
    container.style.position = 'fixed'
    container.style.inset = '0'
    container.style.pointerEvents = 'none'
  }

  // 埋め込み先のWebサイトのテーマに関係なく、gai-agentsのコンポーネントは常にライトテーマを適用させる
  container.dataset['theme'] = 'light'

  // Tailwind CSS を Shadow DOM に適用する。
  // ロード完了時に shadowHost を可視化することで FOUC を防ぐ。
  const resolvedScript = scriptElement ?? document.querySelector('script[src*="embedded.js"]')
  const scriptSrc = resolvedScript?.getAttribute('src') || ''
  const basePath = scriptSrc.substring(0, scriptSrc.lastIndexOf('/') + 1).replace('/scripts', '/styles')
  const styleLink = document.createElement('link')
  styleLink.rel = 'stylesheet'
  styleLink.href = `${basePath}tailwind.css`
  const reveal = () => {
    shadowHost.style.visibility = 'visible'
  }
  styleLink.addEventListener('load', reveal, { once: true })
  styleLink.addEventListener('error', reveal, { once: true })
  // 安全ネット: CSSが5秒ロードされない場合も描画する
  setTimeout(reveal, 5000)
  shadowRoot.appendChild(styleLink)

  // CSS をルートヘッダーではなく Shadow DOM 内に適用するためのプラグイン
  const cache = createCache({
    key: 'gai-agents',
    // Shadow DOM 内にCSSを展開する
    // ref) https://github.com/chakra-ui/chakra-ui/issues/2145
    container: shadowRoot,
    // 埋め込み先WebサイトのCSSの影響を受けないようにするため、
    // 詳細度の高いID指定のセレクタに置き換える
    stylisPlugins: [
      (element) => {
        if (element.type === 'rule' && Array.isArray(element.props)) {
          element.props = element.props.map((prop) =>
            prop.startsWith(`#${container.id}`)
              ? prop
              : ['body', 'html', ':host'].includes(prop)
                ? `#${container.id}`
                : `#${container.id} ${prop}`,
          )
        }
      },
    ],
  })

  // フォントを追加（ページ内で1回だけ、preconnect は embedded.tsx 側で注入済み）
  if (!document.getElementById(FONT_LINK_ID)) {
    const link = document.createElement('link')
    link.id = FONT_LINK_ID
    link.rel = 'stylesheet'
    link.href = FONT_HREF
    document.head.appendChild(link)
  }
  // モーダル用のコンテナを追加
  const modalContainer = shadowRoot.appendChild(document.createElement('div'))
  modalContainer.id = 'modal-container'

  createRoot(container).render(
    <CacheProvider value={cache}>
      <Suspense fallback={null}>
        {cloneElement(node as React.ReactElement, { containerRef: modalContainer, shadowHost, container })}
      </Suspense>
    </CacheProvider>,
  )
}
